import { useEffect, useState } from 'react';
import { Database, Server, AlertCircle } from 'lucide-react';

interface DODatabase {
  id: string;
  name: string;
  engine: string;
  version: string;
  status: string;
  size: string;
  region: string;
  created_at: string;
}

export default function Databases() {
  const [databases, setDatabases] = useState<DODatabase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api/databases')
      .then((r) => {
        if (!r.ok) throw new Error('Failed to load databases');
        return r.json();
      })
      .then((data) => {
        setDatabases(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  const statusColors: Record<string, string> = {
    online: 'text-green-400 bg-green-400/10',
    creating: 'text-yellow-400 bg-yellow-400/10',
    resizing: 'text-blue-400 bg-blue-400/10',
    migrating: 'text-blue-400 bg-blue-400/10',
    forking: 'text-blue-400 bg-blue-400/10',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-white">Database Management</h1>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <p className="text-red-400">{error}</p>
        </div>
      )}

      <div className="bg-gray-800 rounded-xl p-6 mb-8">
        <h2 className="text-lg font-semibold text-white mb-4">
          About Database Management
        </h2>
        <p className="text-gray-400 mb-4">
          SiteHub uses a shared PostgreSQL cluster to provide databases for your sites.
          Each site can have its own database within the shared cluster.
        </p>
        <div className="flex items-start gap-3 p-4 bg-yellow-500/10 rounded-lg">
          <AlertCircle className="w-5 h-5 text-yellow-400 mt-0.5" />
          <div>
            <p className="text-yellow-400 font-medium">Cost Information</p>
            <p className="text-sm text-gray-400">
              Database clusters start at $15/month. Provision databases only when needed.
            </p>
          </div>
        </div>
      </div>

      {/* Database Clusters */}
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white">Database Clusters</h2>
        </div>

        {databases.length > 0 ? (
          <div className="divide-y divide-gray-700">
            {databases.map((db) => (
              <div key={db.id} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-lg bg-gray-700">
                      <Server className="w-6 h-6 text-primary-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-white">{db.name}</h3>
                      <p className="text-sm text-gray-400">
                        {db.engine.toUpperCase()} {db.version} • {db.region}
                      </p>
                    </div>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      statusColors[db.status] || 'text-gray-400 bg-gray-400/10'
                    }`}
                  >
                    {db.status}
                  </span>
                </div>

                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Size</p>
                    <p className="text-white">{db.size}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Engine</p>
                    <p className="text-white">{db.engine}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Version</p>
                    <p className="text-white">{db.version}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Created</p>
                    <p className="text-white">
                      {new Date(db.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Database className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">
              No database clusters
            </h3>
            <p className="text-gray-400 max-w-md mx-auto">
              Database clusters will be created automatically when you provision
              a database for a site that needs one.
            </p>
          </div>
        )}
      </div>

      {/* Site Databases Info */}
      <div className="mt-8 bg-gray-800 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4">
          Provisioning Databases for Sites
        </h2>
        <p className="text-gray-400 mb-4">
          To provision a database for a site:
        </p>
        <ol className="list-decimal list-inside space-y-2 text-gray-400">
          <li>Go to the site's detail page</li>
          <li>Click "Provision Database" in the settings section</li>
          <li>The database will be created in the shared cluster</li>
          <li>Connection details will be available in the site settings</li>
        </ol>
      </div>
    </div>
  );
}
